import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { CheckCircle2, AlertCircle, Loader2, ChevronRight } from "lucide-react";
import { fetchAPI } from "../lib/utils";

export default function VerifyEmail() {
	const navigate = useNavigate();
	const { t } = useLanguage();
	const [searchParams] = useSearchParams();
	const token = (searchParams.get("token") || "").trim();

	const [status, setStatus] = useState(token ? "loading" : "error");
	const [message, setMessage] = useState(
		token ? "" : t("verifyEmailInvalidLink"),
	);
	const attempted = useRef(false);

	useEffect(() => {
		if (!token || attempted.current) return;
		attempted.current = true;
		(async () => {
			try {
				const res = await fetchAPI("/users/change-email/confirm", {
					method: "POST",
					body: JSON.stringify({ token }),
				});
				setStatus("success");
				setMessage(res?.message || t("verifyEmailSuccess"));
			} catch (err) {
				setStatus("error");
				setMessage(err?.message || t("verifyEmailError"));
			}
		})();
	}, [token, t]);

	return (
		<div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
			<Card className="w-full max-w-md bg-card border-border rounded-sm">
				<CardHeader className="text-center">
					<CardTitle className="font-heading uppercase tracking-wider text-2xl">
						{t("verifyEmailTitle")}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-5 text-center">
					{status === "loading" && (
						<div className="flex items-center gap-2 justify-center text-muted-foreground text-sm">
							<Loader2 className="w-4 h-4 animate-spin" />
							{t("verifyEmailLoading")}
						</div>
					)}
					{status === "success" && (
						<div className="flex items-center gap-2 justify-center text-acid text-sm">
							<CheckCircle2 className="w-4 h-4" />
							{message}
						</div>
					)}
					{status === "error" && (
						<div className="flex items-center gap-2 justify-center text-blaze text-sm">
							<AlertCircle className="w-4 h-4" />
							{message}
						</div>
					)}
					{status !== "loading" && (
						<Button
							onClick={() => navigate("/login")}
							className="w-full h-11 bg-volt hover:bg-volt/90 text-white font-bold uppercase tracking-wider rounded-sm"
						>
							{t("goToLogin")}
							<ChevronRight className="w-4 h-4 ml-2" />
						</Button>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
